import { Ticket, WidgetSummary, TicketStats, Resource, TicketNote, TimeEntry, CalendarTimeEntry, CalendarMonthData, Feedback, FeedbackStatus, TimeTechProject, ForwardedRequest, FCRStatus, FCREmail, Contact, UserNotificationSettings, TechLocation, CompanyLocation, CompanyLocationResponse, MissingCompany, RecurringTimeEntry, MileageEntry, ExpenseEntry, IntakeSession, DeviceRegistration, DeviceScanResult } from './types';

const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Helper function to make authenticated API requests
 */
export async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    cache: 'no-store', // Prevent browser caching - ensures fresh data on refresh
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorBody = await response.text();
      // Try to parse as JSON for detailed error
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage = errorJson.details || errorJson.error || errorJson.message || errorBody;
      } catch {
        // Not JSON, use raw text (but truncate if too long)
        errorMessage = errorBody.length > 200 ? errorBody.substring(0, 200) + '...' : errorBody;
      }
    } catch {
      // Could not read body
    }
    throw new Error(`API Error ${response.status}: ${errorMessage}`);
  }

  const result: ApiResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Unknown API error');
  }

  return result.data as T;
}

/**
 * Get widget summary data for dashboard
 */
export async function getWidgetSummary(startDate?: string, endDate?: string): Promise<WidgetSummary> {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return fetchWithAuth<WidgetSummary>(`/widget-summary${query ? `?${query}` : ''}`);
}

/**
 * Get list of tickets with optional filters
 */
export async function getTickets(params?: {
  status?: string;
  priority?: string;
  companyId?: number;
  queueId?: number | number[]; // Single or multiple queue IDs
  unassigned?: boolean;
  limit?: number;
  page?: number;
  live?: boolean; // If true, fetch directly from AutoTask (slower but accurate)
}): Promise<Ticket[]> {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.status) searchParams.set('status', params.status);
    if (params.priority) searchParams.set('priority', params.priority);
    if (params.companyId) searchParams.set('companyId', String(params.companyId));
    if (params.queueId) {
      // Support both single ID and array of IDs
      const queueIds = Array.isArray(params.queueId) ? params.queueId : [params.queueId];
      searchParams.set('queueId', queueIds.join(','));
    }
    if (params.unassigned) searchParams.set('unassigned', 'true');
    // Backend uses pageSize, not limit
    if (params.limit) searchParams.set('pageSize', String(params.limit));
    if (params.page) searchParams.set('page', String(params.page));
  }

  const queryString = searchParams.toString();

  // Use live endpoint for status-filtered queries (always accurate)
  if (params?.live && params?.status) {
    return fetchWithAuth<Ticket[]>(`/tickets/live${queryString ? `?${queryString}` : ''}`);
  }

  const endpoint = `/tickets${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth<Ticket[]>(endpoint);
}

/**
 * Get ticket by ID
 */
export async function getTicketById(id: number): Promise<Ticket> {
  return fetchWithAuth<Ticket>(`/tickets/${id}`);
}

/**
 * Get ticket statistics for dashboard
 */
export async function getTicketStats(): Promise<TicketStats> {
  return fetchWithAuth<TicketStats>('/tickets/stats');
}

// ─── Stale-ticket audit ────────────────────────────────────────────────────

export interface AuditSummary {
  total: number;
  anomalies: number;
  stuckBeyondClose: number;
  missingNudge: number;
  missingFlag: number;
  snoozed: number;
}

export interface AuditRow {
  autoTaskId: number;
  ticketNumber: string;
  title: string;
  status: string;
  companyName: string;
  assignedResourceId?: number;
  assignedResourceName?: string;
  lastActivityDate: string;
  daysInStatus: number;
  flow: 'waiting_customer' | 'waiting_on_us' | 'other';
  expectedState: 'healthy' | 'wc_flagged' | 'wc_nudged' | 'wc_closed' | 'wou_flagged' | 'wou_emailed';
  actualState: 'untracked' | 'tracked-no-flag' | 'snoozed' | 'flagged' | 'nudged' | 'closed' | 'emailed';
  trackingStartedAt?: string;
  flaggedAt?: string;
  nudgedAt?: string;
  autoClosedAt?: string;
  adminEmailSentAt?: string;
  snoozedUntil?: string;
  isAnomaly: boolean;
  anomalyReason?: string;
  daysSinceTrackingStarted?: number;
  autoCloseAfterDate?: string;
  autoCloseWarnedAt?: string;
}

export async function getTicketsAudit(): Promise<{ summary: AuditSummary; rows: AuditRow[] }> {
  return fetchWithAuth<{ summary: AuditSummary; rows: AuditRow[] }>('/tickets/audit');
}

/**
 * Ticket field option (status/priority)
 */
export interface TicketFieldOption {
  id: number;
  name: string;
  isActive: boolean;
  isSystem: boolean;
  sortOrder?: number;
}

/**
 * Get ticket field options (status, priority) from AutoTask
 */
export async function getTicketFieldOptions(): Promise<{
  statuses: TicketFieldOption[];
  priorities: TicketFieldOption[];
}> {
  return fetchWithAuth('/tickets/field-options');
}

/**
 * Sync result data from the API
 */
export interface SyncResult {
  syncId: string;
  ticketsProcessed: number;
  created: number;
  updated: number;
  errors: number;
  durationMs: number;
  status: 'success' | 'partial' | 'failed';
}

/**
 * Trigger a sync operation to fetch latest tickets from AutoTask
 */
export async function triggerSync(): Promise<SyncResult> {
  return fetchWithAuth<SyncResult>('/sync/trigger', {
    method: 'POST',
  });
}

/**
 * Full sync result including companies
 */
export interface FullSyncResult {
  syncId: string;
  companiesProcessed: number;
  ticketsProcessed: number;
  ticketsCreated: number;
  ticketsUpdated: number;
  errors: number;
  durationMs: number;
  status: 'success' | 'partial' | 'failed';
  // DEBUG: Sample ticket info from AutoTask
  debug?: {
    totalTickets: number;
    ticketsWithQueue: number;
    ticketsWithoutQueue: number;
    queueLookupCount: number;
    allQueues: [string, string][];
    missingQueueIds: number[];
    sampleWithQueue: { ticketNumber: string; queueID: number; resolvedName: string | null } | null;
    sampleWithoutQueue: { ticketNumber: string; queueID: any } | null;
    unassignedSample: { ticketNumber: string; queueID: number } | null;
    allKeys: string[];
  };
}

/**
 * Company sync result
 */
export interface CompanySyncResult {
  syncId: string;
  companiesProcessed: number;
  errors: number;
  durationMs: number;
  status: 'success' | 'partial' | 'failed';
}

/**
 * Trigger a full sync (companies first, then tickets)
 */
export async function triggerFullSync(): Promise<FullSyncResult> {
  return fetchWithAuth<FullSyncResult>('/sync/full', {
    method: 'POST',
  });
}

/**
 * Trigger a company sync
 */
export async function triggerCompanySync(): Promise<CompanySyncResult> {
  return fetchWithAuth<CompanySyncResult>('/sync/companies', {
    method: 'POST',
  });
}

/**
 * Resource sync result
 */
export interface ResourceSyncResult {
  syncId: string;
  resourcesProcessed: number;
  resourcesCreated: number;
  resourcesUpdated: number;
  errors: number;
  durationMs: number;
  status: 'success' | 'partial' | 'failed';
}

/**
 * Trigger a resource sync (technicians from AutoTask)
 */
export async function triggerResourceSync(): Promise<ResourceSyncResult> {
  return fetchWithAuth<ResourceSyncResult>('/sync/resources', {
    method: 'POST',
  });
}

/**
 * Backfill result (historical ticket sync)
 */
export interface BackfillResult {
  syncId: string;
  days: number;
  dateRange: {
    start: string;
    end: string;
  };
  ticketsProcessed: number;
  ticketsCreated: number;
  ticketsUpdated: number;
  errors: number;
  durationMs: number;
  status: 'success' | 'partial' | 'failed';
}

/**
 * Trigger a backfill sync (one-time historical data import)
 * @param startDaysAgo Start of range (0 = today)
 * @param endDaysAgo End of range (30 = 30 days ago)
 */
export async function triggerBackfill(startDaysAgo: number = 0, endDaysAgo: number = 30): Promise<BackfillResult> {
  return fetchWithAuth<BackfillResult>(`/sync/backfill?startDaysAgo=${startDaysAgo}&endDaysAgo=${endDaysAgo}`, {
    method: 'POST',
  });
}

/**
 * Format relative time from ISO date string
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}

/**
 * Get list of resources (technicians)
 */
export async function getResources(): Promise<Resource[]> {
  return fetchWithAuth<Resource[]>('/resources');
}

/**
 * Company for dropdown selection
 */
export interface CompanyOption {
  id: string;
  autoTaskId: number;
  companyName: string;
  isActive: boolean;
  source?: 'autotask' | 'manual';  // 'manual' = custom client (not in AutoTask)
}

/**
 * Get list of active companies for dropdowns
 */
export async function getActiveCompanies(): Promise<CompanyOption[]> {
  return fetchWithAuth<CompanyOption[]>('/companies?active=true&all=true');
}

/**
 * Created ticket response
 */
export interface CreatedTicket {
  id: number;
  ticketNumber: string;
  title: string;
  companyID: number;
  status: number;
  priority: number;
  queueID?: number;
}

/**
 * Create a new ticket in AutoTask
 */
export async function createTicketInAutoTask(ticket: {
  companyID: number;
  title: string;
  description?: string;
  queueID?: number;
  priority?: number;
  dueDateTime?: string;
  contactID?: number;
  issueType?: number;
  status?: number;  // 1=New, 5=Complete
  assignedResourceID?: number;
  assignedResourceRoleID?: number;
}): Promise<CreatedTicket> {
  // Import debug logger dynamically to avoid circular deps
  const { addDebugLog } = await import('../components/FloatingDebugPanel');

  addDebugLog({
    type: 'request',
    source: 'AutoTask',
    message: `POST /tickets (Create Ticket)`,
    details: JSON.stringify(ticket, null, 2),
  });

  try {
    const result = await fetchWithAuth<CreatedTicket>('/tickets', {
      method: 'POST',
      body: JSON.stringify(ticket),
    });

    addDebugLog({
      type: 'success',
      source: 'AutoTask',
      message: `Ticket created: ${result.ticketNumber} (ID: ${result.id})`,
    });

    return result;
  } catch (err) {
    addDebugLog({
      type: 'error',
      source: 'AutoTask',
      message: `Create ticket failed: ${err instanceof Error ? err.message : String(err)}`,
    });
    throw err;
  }
}

/**
 * Update a ticket (accept, change status, priority, etc.)
 */
export async function updateTicket(
  ticketId: number,
  updates: {
    assignedResourceID?: number;
    assignedResourceRoleID?: number;
    status?: number;
    priority?: number;
    resolution?: string;
    companyID?: number;
    contactID?: number;
    /** Cosmos-only. `null` clears the unverified caller details (v1.67.0). */
    unverifiedCaller?: null;
  }
): Promise<Ticket> {
  return fetchWithAuth<Ticket>(`/tickets/${ticketId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Set / update auto-close flags on a ticket. Uses the wrapped-body mode
 * so Cosmos-only fields are routed correctly without an AutoTask round-trip.
 * Pass null on a field to clear it (e.g. cancel auto-close).
 */
export async function setTicketAutoClose(
  ticketId: number,
  fields: {
    autoCloseAfterDate?: string | null;
    autoCloseIfInactiveDays?: number | null;
    autoCloseStatus?: number | null;
    autoCloseWarnDays?: number | null;
  },
  reason?: string
): Promise<{ id: number; ticketNumber: string; updatedFields: string[] }> {
  return fetchWithAuth(`/tickets/${ticketId}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields, reason }),
  });
}

/**
 * Set Project Management overlay fields. Same wrapped-body pattern as
 * setTicketAutoClose — these are Cosmos-only fields routed through the
 * fields:{...} mode in PatchTicket. Pass null on a field to clear it.
 * Used by the ProjectStatusPanel on isProject tickets; consumed by
 * OverSite V3's PM surface via the /api/tickets response.
 */
import type { ProjectStatusUpdate } from './types';
export async function setTicketProjectStatus(
  ticketId: number,
  fields: {
    percentComplete?: number | null;
    health?: 'green' | 'yellow' | 'red' | null;
    projectStatusNote?: string | null;
    projectStatusUpdatedBy?: number;
    projectUpdates?: ProjectStatusUpdate[];
  },
  reason?: string
): Promise<{ id: number; ticketNumber: string; updatedFields: string[] }> {
  return fetchWithAuth(`/tickets/${ticketId}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields, reason }),
  });
}

/**
 * Snooze stale ticket tracking for a specified duration
 */
export async function snoozeTicket(
  ticketId: number,
  days: 3 | 7 | 14 | 30,
  resourceId: number
): Promise<{ ticketNumber: string; snoozedUntil: string; days: number }> {
  return fetchWithAuth(`/tickets/${ticketId}/snooze`, {
    method: 'PATCH',
    body: JSON.stringify({ days, resourceId }),
  });
}

/**
 * Toggle isProject flag on a ticket (Cosmos-only, not synced to AutoTask)
 */
export async function toggleProjectFlag(
  ticketId: number,
  isProject: boolean
): Promise<{ isProject: boolean }> {
  return fetchWithAuth(`/tickets/${ticketId}/project`, {
    method: 'PATCH',
    body: JSON.stringify({ isProject }),
  });
}

/**
 * Nudge customer - sends follow-up note and changes status to Waiting Customer
 */
export async function nudgeTicket(
  ticketId: number,
  resourceId: number
): Promise<{ ticketNumber: string; status: string; nudgedAt: string }> {
  return fetchWithAuth(`/tickets/${ticketId}/nudge`, {
    method: 'POST',
    body: JSON.stringify({ resourceId }),
  });
}

/**
 * Add a note to a ticket
 */
export async function addTicketNote(
  ticketId: number,
  note: {
    description: string;
    noteType?: number;  // 1 = Internal, 2 = Customer Visible
    publish?: number;   // 1 = All Users, 2 = Internal Only
    creatorResourceID?: number;  // The tech who created the note
  }
): Promise<void> {
  await fetchWithAuth(`/tickets/${ticketId}/notes`, {
    method: 'POST',
    body: JSON.stringify(note),
  });
}

/**
 * Time entry creation result
 */
export interface TimeEntryResult {
  id: number;
  timetech?: {
    success: boolean;
    timeEntryId?: number | null;
    error?: string;
  };
  cosmos?: {
    success: boolean;
    id?: string;
    error?: string;
  };
}

/**
 * Add a time entry to a ticket
 * Note: This function handles the full API response (including timetech/cosmos)
 * instead of using fetchWithAuth which only returns result.data
 */
export async function addTimeEntry(
  ticketId: number,
  entry: {
    resourceID: number;
    roleID?: number;
    hoursWorked: number;
    summaryNotes: string;
    dateWorked?: string;
    // Optional: pass these to avoid lookup (useful for newly created tickets)
    companyId?: number;
    companyName?: string;
    ticketNumber?: string;
    ticketTitle?: string;
    // TimeTech project (for clients that require SourceTypeId=3)
    projectId?: number;
    projectName?: string;
    // Bid-job for billing categorization
    bidJobNumber?: string;
    bidJobDescription?: string;
  }
): Promise<TimeEntryResult> {
  // Import debug logger dynamically to avoid circular deps
  const { addDebugLog } = await import('../components/FloatingDebugPanel');

  const url = `${API_BASE}/tickets/${ticketId}/time-entries`;

  addDebugLog({
    type: 'request',
    source: 'AutoTask',
    message: `POST /tickets/${ticketId}/time-entries`,
    details: JSON.stringify(entry, null, 2),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorBody = await response.text();
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage = errorJson.details || errorJson.error || errorJson.message || errorBody;
      } catch {
        errorMessage = errorBody.length > 200 ? errorBody.substring(0, 200) + '...' : errorBody;
      }
    } catch {
      // Could not read body
    }
    addDebugLog({
      type: 'error',
      source: 'AutoTask',
      message: `HTTP ${response.status}: ${errorMessage}`,
    });
    throw new Error(`API Error ${response.status}: ${errorMessage}`);
  }

  // Parse the full response - timetech and cosmos are at top level, not inside data
  const result = await response.json();

  addDebugLog({
    type: 'response',
    source: 'AutoTask',
    message: `Response received (success: ${result.success})`,
    details: JSON.stringify(result, null, 2),
  });

  if (!result.success) {
    addDebugLog({
      type: 'error',
      source: 'AutoTask',
      message: `API Error: ${result.error}`,
    });
    throw new Error(result.error || 'Unknown API error');
  }

  // Log AutoTask result
  addDebugLog({
    type: 'success',
    source: 'AutoTask',
    message: `Time entry created (ID: ${result.data?.id})`,
  });

  // Log TimeTech result
  if (result.timetech) {
    if (result.timetech.success) {
      addDebugLog({
        type: 'success',
        source: 'TimeTech',
        message: `Time entry created (ID: ${result.timetech.timeEntryId})`,
      });
    } else {
      addDebugLog({
        type: 'error',
        source: 'TimeTech',
        message: `Failed: ${result.timetech.error}`,
      });
    }
  } else {
    addDebugLog({
      type: 'warning',
      source: 'TimeTech',
      message: 'No TimeTech response in API result',
    });
  }

  // Log Cosmos result
  if (result.cosmos) {
    if (result.cosmos.success) {
      addDebugLog({
        type: 'success',
        source: 'Cosmos',
        message: `Cache updated (ID: ${result.cosmos.id})`,
      });
    } else {
      addDebugLog({
        type: 'warning',
        source: 'Cosmos',
        message: `Cache failed: ${result.cosmos.error}`,
      });
    }
  }

  // Return combined result with data.id and top-level timetech/cosmos
  return {
    id: result.data?.id || 0,
    timetech: result.timetech,
    cosmos: result.cosmos,
  };
}

/**
 * Add time entry for custom client (no AutoTask ticket)
 * These entries are stored locally only
 */
export async function addCustomClientTimeEntry(entry: {
  resourceId: number;
  resourceName: string;
  companyId: number;
  companyName: string;
  hoursWorked: number;
  summaryNotes: string;
  dateWorked: string;
}): Promise<{ id: string; success: boolean }> {
  return fetchWithAuth<{ id: string; success: boolean }>('/custom-time-entries', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/**
 * Refresh a ticket from AutoTask and update local database
 * Call this when opening a ticket modal to ensure fresh data
 */
export async function refreshTicket(ticketId: number): Promise<Ticket> {
  return fetchWithAuth<Ticket>(`/tickets/${ticketId}/refresh`, {
    method: 'POST',
  });
}

/**
 * Get notes for a ticket
 */
export async function getTicketNotes(ticketId: number): Promise<TicketNote[]> {
  return fetchWithAuth<TicketNote[]>(`/tickets/${ticketId}/notes`);
}

/**
 * Get time entries for a ticket
 */
export async function getTicketTimeEntries(ticketId: number): Promise<TimeEntry[]> {
  return fetchWithAuth<TimeEntry[]>(`/tickets/${ticketId}/time-entries`);
}

/**
 * TimeTech time entry (from legacy system)
 */
export interface TimeTechTimeEntry {
  TimeEntryId: number;
  UserId: number;
  ClientId: number;
  DateEntered: string;
  Hours: number;
  Minutes: number;
  Description: string;
  SourceTypeId: number;
  SourceId: string;
  DisplayText: string;
  clientName: string; // Enriched by API
}

/**
 * Get time entries from TimeTech for a user and date range
 * @param userId TimeTech user ID
 * @param startDate Start date (YYYY-MM-DD)
 * @param endDate End date (YYYY-MM-DD)
 */
export async function getTimeTechTimeEntries(
  userId: number,
  startDate: string,
  endDate: string
): Promise<TimeTechTimeEntry[]> {
  return fetchWithAuth<TimeTechTimeEntry[]>(
    `/timetech/entries?userId=${userId}&startDate=${startDate}&endDate=${endDate}`
  );
}

/**
 * Get TimeTech projects for a company (by AutoTask company ID)
 * Returns empty array if company has no projects configured
 */
export async function getTimeTechProjectsForCompany(autoTaskCompanyId: number): Promise<TimeTechProject[]> {
  return fetchWithAuth<TimeTechProject[]>(`/timetech/projects/${autoTaskCompanyId}`);
}

/**
 * Sync TimeTech entries result
 */
export interface SyncTimeTechResult {
  imported: number;
  skipped: number;
  total: number;
  errors?: string[];
}

/**
 * Sync time entries FROM TimeTech into our database (READ-ONLY from TimeTech)
 */
export async function syncTimeTechEntries(
  userId: number,
  startDate: string,
  endDate: string
): Promise<SyncTimeTechResult> {
  return fetchWithAuth<SyncTimeTechResult>('/timetech/sync', {
    method: 'POST',
    body: JSON.stringify({ userId, startDate, endDate }),
  });
}

/**
 * Duplicate check result
 */
export interface DuplicateCheckResult {
  totalUniqueTicketNumbers: number;
  duplicateCount: number;
  duplicates: { ticketNumber: string; count: number }[];
}

/**
 * Check for duplicate tickets in the database
 */
export async function checkDuplicates(): Promise<DuplicateCheckResult> {
  const response = await fetch(`${API_BASE}/debug/duplicates`);
  if (!response.ok) {
    throw new Error(`API Error ${response.status}: ${response.statusText}`);
  }
  const result = await response.json();
  return result;
}

/**
 * Debug queue data from AutoTask
 */
export interface DebugQueuesResult {
  success: boolean;
  // Ticket queues from queueID picklist
  queueCount: number;
  lookupCount: number;
  queues: Array<{ id: number; name: string; isActive?: boolean }>;
  lookup: Record<string, string>;  // { "29683482": "SaaS Alerts" }
  // Departments (NOT ticket queues - for reference)
  departmentCount: number;
  departments: Array<{ id: number; name: string; description?: string; primaryLocationID?: number }>;
  error?: string;
}

export async function debugQueues(): Promise<DebugQueuesResult> {
  const response = await fetch(`${API_BASE}/debug/queues`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`API Error ${response.status}: ${text}`);
  }
  return response.json();
}

/**
 * Queue lookup map (ID to name)
 */
export type QueueLookup = Record<string, string>;

/**
 * Get queue lookup for resolving queue IDs to names
 * Uses the debug/queues endpoint which returns the picklist data
 */
export async function getQueueLookup(): Promise<QueueLookup> {
  const result = await debugQueues();
  return result.lookup;
}

/**
 * Sync status/history response
 */
export interface SyncHistoryRecord {
  id: string;
  syncType: string;
  status: 'success' | 'failed' | 'partial';
  startTime: string;
  endTime: string;
  durationMs: number;
  durationFormatted: string;
  counts: {
    ticketsProcessed: number;
    resourcesProcessed: number;
    companiesProcessed: number;
    created: number;
    updated: number;
    deleted: number;
  };
  errorCount?: number;
  errors?: string[];
  triggeredBy: 'manual' | 'scheduled';
  notes?: string;
}

export interface SyncStatusResponse {
  success: boolean;
  data: SyncHistoryRecord[];
  count: number;
  summary?: {
    last24Hours: {
      total: number;
      success: number;
      failed: number;
      partial: number;
    };
    latestByType: Record<string, { id: string; status: string; startTime: string; endTime: string } | null>;
  };
}

/**
 * Get sync status/history
 * Note: fetchWithAuth unwraps the response, so this returns the data array directly
 */
export async function getSyncStatus(limit: number = 10, type?: string): Promise<SyncHistoryRecord[]> {
  let url = `/sync/status?limit=${limit}`;
  if (type) {
    url += `&type=${type}`;
  }
  return fetchWithAuth<SyncHistoryRecord[]>(url);
}

// ============================================================================
// Calendar Time Entry API
// ============================================================================

/**
 * Get time entries for a specific month (calendar view)
 * @param resourceId - Filter by AutoTask resource ID (for employees)
 * @param resourceName - Filter by resource name (for contractors)
 */
export async function getCalendarMonth(
  year: number,
  month: number,
  resourceId?: number,
  resourceName?: string
): Promise<CalendarMonthData> {
  const params = new URLSearchParams();
  if (resourceId) {
    params.set('resourceId', String(resourceId));
  } else if (resourceName) {
    params.set('resourceName', resourceName);
  }
  const queryString = params.toString();
  const url = `/time-entries/month/${year}/${month}${queryString ? `?${queryString}` : ''}`;
  return fetchWithAuth<CalendarMonthData>(url);
}

/**
 * Get time entries with filters
 */
export async function getCalendarTimeEntries(params?: {
  resourceId?: number;
  companyId?: number;
  ticketId?: number;
  dateFrom?: string;
  dateTo?: string;
  syncStatus?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: CalendarTimeEntry[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params) {
    if (params.resourceId) searchParams.set('resourceId', String(params.resourceId));
    if (params.companyId) searchParams.set('companyId', String(params.companyId));
    if (params.ticketId) searchParams.set('ticketId', String(params.ticketId));
    if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params.syncStatus) searchParams.set('syncStatus', params.syncStatus);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  }
  const query = searchParams.toString();
  return fetchWithAuth(`/time-entries${query ? `?${query}` : ''}`);
}

/**
 * Create a new calendar time entry
 */
export async function createCalendarTimeEntry(entry: {
  ticketId: number;
  ticketNumber: string;
  resourceId: number;
  resourceName: string;
  roleId?: number;
  dateWorked: string;
  hoursWorked: number;
  startTime?: string;
  endTime?: string;
  summaryNotes: string;
  internalNotes?: string;
  companyId: number;
  companyName: string;
  bidJobNumber?: string;
  bidJobDescription?: string;
  isBillable?: boolean;
}): Promise<CalendarTimeEntry> {
  return fetchWithAuth<CalendarTimeEntry>('/time-entries', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/**
 * Update a calendar time entry
 * Returns the updated entry with optional timetechWarning field if TimeTech sync failed
 */
export async function updateCalendarTimeEntry(
  id: string,
  updates: Partial<{
    hoursWorked: number;
    dateWorked: string;
    startTime: string;
    endTime: string;
    summaryNotes: string;
    internalNotes: string;
    bidJobNumber: string;
    bidJobDescription: string;
    isBillable: boolean;
    updatedBy: number;
  }>
): Promise<CalendarTimeEntry & { timetechWarning?: string }> {
  return fetchWithAuth<CalendarTimeEntry & { timetechWarning?: string }>(`/time-entries/${id}?force=true`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a calendar time entry
 * Uses force=true to allow deleting entries that have been synced to AutoTask
 */
export async function deleteCalendarTimeEntry(id: string): Promise<void> {
  await fetchWithAuth(`/time-entries/${id}?force=true`, {
    method: 'DELETE',
  });
}

/**
 * Retry failed TimeTech sync for a time entry
 */
export async function retryTimeTechSync(id: string): Promise<{ timetechTimeEntryId?: number }> {
  const response = await fetch(`/api/time-entries/${id}/retry-timetech`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json() as { success: boolean; data?: { timetechTimeEntryId?: number }; error?: string };
  if (!result.success) {
    throw new Error(result.error || 'Failed to retry TimeTech sync');
  }
  return result.data || {};
}

// ============================================================================
// Batch Time Entries
// ============================================================================

export interface BatchTimeEntryEntry {
  ticketId: number;
  ticketNumber: string;
  ticketTitle?: string;
  hoursWorked: number;
  closeTicket?: boolean;
  summaryNotes?: string;
}

export interface BatchTimeEntryRequest {
  resourceId: number;
  resourceName: string;
  dateWorked: string;
  summaryNotes: string;
  companyId: number;
  companyName: string;
  bidJobNumber?: string;
  bidJobDescription?: string;
  projectId?: number;
  projectName?: string;
  entries: BatchTimeEntryEntry[];
}

export interface BatchTimeEntryResultEntry {
  ticketId: number;
  ticketNumber: string;
  success: boolean;
  error?: string;
  closeRequested?: boolean;
  closedTicket?: boolean;
  closeError?: string;
}

export interface BatchTimeEntryResult {
  success: boolean;
  total: number;
  successCount: number;
  failCount: number;
  results: BatchTimeEntryResultEntry[];
}

/**
 * Submit time entries for multiple tickets at once (batch)
 */
export async function addBatchTimeEntries(request: BatchTimeEntryRequest): Promise<BatchTimeEntryResult> {
  const url = `${API_BASE}/time-entries/batch`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorBody = await response.text();
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage = errorJson.details || errorJson.error || errorJson.message || errorBody;
      } catch {
        errorMessage = errorBody.length > 200 ? errorBody.substring(0, 200) + '...' : errorBody;
      }
    } catch {
      // Could not read body
    }
    throw new Error(`API Error ${response.status}: ${errorMessage}`);
  }

  const result = await response.json() as BatchTimeEntryResult;
  return result;
}

/**
 * Sync time entries from AutoTask result
 */
export interface SyncTimeEntriesResult {
  total: number;
  synced: number;
  errors: number;
  dateRange: {
    start: string;
    end: string;
  };
}

/**
 * Sync time entries from AutoTask to local database
 */
export async function syncTimeEntriesFromAutoTask(params?: {
  startDate?: string;
  endDate?: string;
  resourceId?: number;
}): Promise<SyncTimeEntriesResult> {
  return fetchWithAuth<SyncTimeEntriesResult>('/time-entries/sync', {
    method: 'POST',
    body: JSON.stringify(params || {}),
  });
}

// ============================================================================
// System Settings
// ============================================================================

export interface SystemSettings {
  timetechEnabled?: boolean;
  timetechBaseUrl?: string;
  [key: string]: unknown;
}

/**
 * Get all system settings
 */
export async function getSystemSettings(): Promise<SystemSettings> {
  return fetchWithAuth<SystemSettings>('/settings');
}

/**
 * Get a specific system setting
 */
export async function getSystemSetting<T = unknown>(key: string): Promise<T> {
  const result = await fetchWithAuth<{ key: string; value: T }>(`/settings/${key}`);
  return result.value;
}

/**
 * Update a system setting
 */
export async function updateSystemSetting<T = unknown>(
  key: string,
  value: T,
  description?: string
): Promise<void> {
  await fetchWithAuth(`/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value, description }),
  });
}

// ============================================================================
// Ticket Merge
// ============================================================================

/**
 * Result of merging tickets
 */
export interface MergeTicketResult {
  currentTicket: string;
  mergedTicket: string;
  message: string;
}

/**
 * Merge a ticket into another ticket
 * - Adds notes to both tickets
 * - Sets merged ticket to Complete
 * - Copies merged ticket's description as note
 */
export async function mergeTicket(
  currentTicketId: number,
  mergeTicketId: number
): Promise<MergeTicketResult> {
  return fetchWithAuth<MergeTicketResult>(`/tickets/${currentTicketId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ mergeTicketId }),
  });
}

/**
 * Get tickets for a company (for merge dropdown)
 * Only returns New and In Progress tickets
 */
export async function getCompanyTicketsForMerge(
  companyId: number,
  excludeTicketId: number
): Promise<Ticket[]> {
  const tickets = await getTickets({
    companyId,
    status: 'New,In Progress',
    limit: 100,
  });
  // Filter out the current ticket
  return tickets.filter(t => t.autoTaskId !== excludeTicketId);
}

/**
 * Search for a ticket by ticket number (e.g., "T20260120.0024")
 * Returns the ticket if found, null otherwise
 */
export async function getTicketByNumber(ticketNumber: string): Promise<Ticket | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('search', ticketNumber);
  searchParams.set('limit', '10');

  const response = await fetchWithAuth(`/api/tickets?${searchParams.toString()}`) as Ticket[];

  // Find exact match by ticket number
  const exactMatch = response.find((t: Ticket) =>
    t.ticketNumber.toLowerCase() === ticketNumber.toLowerCase()
  );

  return exactMatch || null;
}

// ============================================================================
// Feedback API
// ============================================================================

/**
 * Get all feedback submissions (admin view)
 */
export async function getFeedbackList(params?: {
  status?: FeedbackStatus;
  limit?: number;
}): Promise<Feedback[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const queryString = searchParams.toString();
  const url = queryString ? `/feedback?${queryString}` : '/feedback';

  const response = await fetchWithAuth(url);
  return response as Feedback[];
}

/**
 * Get single feedback item with full screenshot
 */
export async function getFeedbackItem(id: string): Promise<Feedback> {
  return fetchWithAuth(`/feedback/${id}`) as Promise<Feedback>;
}

/**
 * Update feedback status
 */
export async function updateFeedback(
  id: string,
  data: {
    status: FeedbackStatus;
    reviewedBy?: string;
    reviewNotes?: string;
  }
): Promise<{ id: string; status: string; reviewedAt: string }> {
  const response = await fetch(`/api/feedback/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update feedback');
  }

  const result = await response.json();
  return result.data;
}

// ============================================================================
// Admin Config
// ============================================================================

/**
 * Contractor configuration
 */
export interface ContractorConfig {
  name: string;
  email: string;
  timetechUserId: number;
}

/**
 * Admin configuration (loaded from settings)
 */
export interface AdminConfig {
  admins: string[];
  contractors: ContractorConfig[];
  hiddenResourceIds: number[];
}

/**
 * Get admin config (admin users, contractors, and hidden resources)
 */
export async function getAdminConfig(): Promise<AdminConfig> {
  return fetchWithAuth<AdminConfig>('/sys/config');
}

/**
 * Update admin users list
 */
export async function updateAdminUsers(admins: string[]): Promise<{ admins: string[] }> {
  return fetchWithAuth<{ admins: string[] }>('/sys/admins', {
    method: 'PUT',
    body: JSON.stringify({ admins }),
  });
}

/**
 * Update contractors list
 */
export async function updateContractors(contractors: ContractorConfig[]): Promise<{ contractors: ContractorConfig[] }> {
  return fetchWithAuth<{ contractors: ContractorConfig[] }>('/sys/contractors', {
    method: 'PUT',
    body: JSON.stringify({ contractors }),
  });
}

/**
 * Update hidden resource IDs (resources hidden from dropdowns)
 */
export async function updateHiddenResources(hiddenResourceIds: number[]): Promise<{ hiddenResourceIds: number[] }> {
  return fetchWithAuth<{ hiddenResourceIds: number[] }>('/sys/hidden-resources', {
    method: 'PUT',
    body: JSON.stringify({ hiddenResourceIds }),
  });
}

// ============================================================================
// Bid-Job API
// ============================================================================

/**
 * Bid-job configuration
 */
export interface BidJob {
  id: string;
  companyId: number;
  companyName: string;
  bidJobNumber: string;
  bidJobDescription: string;
  isActive: boolean;
  isCommon: boolean;
  isDefault: boolean;
  defaultForResourceId?: number; // If set, this is the default only for this specific tech
  defaultForResourceName?: string; // Display name of the resource
}

/**
 * Get bid-jobs for a specific company (includes common bid-jobs)
 * Returns sorted by bidJobNumber
 */
export async function getBidJobsForCompany(companyId: number): Promise<BidJob[]> {
  return fetchWithAuth<BidJob[]>(`/bidjobs/company/${companyId}`);
}

/**
 * Get the default bid-job for a company and optionally a specific tech
 * Logic:
 * 1. First look for tech-specific default for this company
 * 2. Then look for tech-specific common default
 * 3. Then look for company-specific default (for everyone)
 * 4. Then look for common default (for everyone)
 * 5. Then fallback to first common bid-job with "Help Desk" description
 * 6. Then fallback to first available bid-job
 */
export function getDefaultBidJob(bidJobs: BidJob[], companyId: number, resourceId?: number): BidJob | null {
  if (bidJobs.length === 0) return null;

  // 1. Tech-specific default for this company
  if (resourceId) {
    const techCompanyDefault = bidJobs.find(bj =>
      bj.companyId === companyId && bj.isDefault && bj.defaultForResourceId === resourceId
    );
    if (techCompanyDefault) return techCompanyDefault;

    // 2. Tech-specific common default
    const techCommonDefault = bidJobs.find(bj =>
      bj.isCommon && bj.isDefault && bj.defaultForResourceId === resourceId
    );
    if (techCommonDefault) return techCommonDefault;
  }

  // 3. Company-specific default (for everyone - no specific tech)
  const companyDefault = bidJobs.find(bj =>
    bj.companyId === companyId && bj.isDefault && !bj.defaultForResourceId
  );
  if (companyDefault) return companyDefault;

  // 4. Common default (for everyone - no specific tech)
  const commonDefault = bidJobs.find(bj =>
    bj.isCommon && bj.isDefault && !bj.defaultForResourceId
  );
  if (commonDefault) return commonDefault;

  // 5. Common "Help Desk" / HDP fallback
  const helpDeskFallback = bidJobs.find(bj => {
    if (!bj.isCommon) return false;
    const desc = bj.bidJobDescription.toLowerCase().replace(/\s+/g, '');
    return desc.includes('helpdesk') || bj.bidJobNumber.toUpperCase().startsWith('HDP');
  });
  if (helpDeskFallback) return helpDeskFallback;

  // 6. First available
  return bidJobs[0];
}

// ============================================================================
// Forwarded Customer Request (FCR) API
// ============================================================================

/**
 * Get list of FCRs with optional status filter
 */
export async function getFCRList(params?: {
  status?: FCRStatus;
  limit?: number;
}): Promise<ForwardedRequest[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const queryString = searchParams.toString();
  const url = queryString ? `/fcr?${queryString}` : '/fcr';

  return fetchWithAuth<ForwardedRequest[]>(url);
}

/**
 * Get count of pending FCRs
 */
export async function getFCRCount(): Promise<number> {
  const result = await fetchWithAuth<{ count: number }>('/fcr/count');
  return result.count;
}

/**
 * Get single FCR with full details
 */
export async function getFCR(id: string): Promise<ForwardedRequest> {
  return fetchWithAuth<ForwardedRequest>(`/fcr/${id}`);
}

/**
 * Get contacts for an FCR's matched company
 */
export async function getFCRContacts(id: string): Promise<Contact[]> {
  return fetchWithAuth<Contact[]>(`/fcr/${id}/contacts`);
}

/**
 * Fetch the full original email from the mailbox for an FCR
 */
export async function getFCREmail(id: string): Promise<FCREmail> {
  return fetchWithAuth<FCREmail>(`/fcr/${id}/email`);
}

/**
 * Fetch the original email for a ticket (looks up the FCR that created it)
 */
export async function getTicketOriginalEmail(ticketId: number): Promise<FCREmail> {
  return fetchWithAuth<FCREmail>(`/tickets/${ticketId}/original-email`);
}

/**
 * Process an FCR - create ticket from the forwarded request
 */
export async function processFCR(
  id: string,
  data: {
    companyId: number;
    contactId?: number;
    createContact?: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    title?: string;
    description?: string;
    queueId?: number;
    priority?: number;
    assignedResourceId?: number;
  }
): Promise<{
  fcrId: string;
  ticketId: number;
  ticketNumber: string;
  contactId?: number;
}> {
  return fetchWithAuth(`/fcr/${id}/process`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Mark an FCR as ignored (not processing)
 */
export async function ignoreFCR(id: string): Promise<{ id: string; status: string }> {
  return fetchWithAuth(`/fcr/${id}/ignore`, {
    method: 'POST',
  });
}

/**
 * Delete an FCR
 */
export async function deleteFCR(id: string): Promise<void> {
  await fetchWithAuth(`/fcr/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Contact API
// ============================================================================

/**
 * Search for a contact by email
 */
export async function searchContactByEmail(email: string): Promise<Contact | null> {
  return fetchWithAuth<Contact | null>(`/contacts/search?email=${encodeURIComponent(email)}`);
}

/**
 * Get contacts for a company
 */
export async function getCompanyContacts(companyId: number, activeOnly = true): Promise<Contact[]> {
  return fetchWithAuth<Contact[]>(`/contacts/company/${companyId}?active=${activeOnly}`);
}

/**
 * Create a new contact
 */
export async function createContact(data: {
  companyId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  title?: string;
}): Promise<Contact> {
  return fetchWithAuth<Contact>('/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Spam Ticket API
// ============================================================================

/**
 * Mark a ticket as spam and auto-close it
 * - Adds "Marked as spam" internal note
 * - Creates 0-hour time entry (if resourceId provided)
 * - Sets status to Complete
 */
export async function markTicketAsSpam(
  ticketId: number,
  resourceId?: number
): Promise<{ ticketId: number; status: string; message: string }> {
  return fetchWithAuth(`/tickets/${ticketId}/spam`, {
    method: 'POST',
    body: JSON.stringify({ resourceId }),
  });
}

// ============================================================================
// Custom Clients API (Non-AutoTask Companies)
// ============================================================================

/**
 * Custom client (non-AutoTask company)
 */
export interface CustomClient {
  id: string;
  autoTaskId: number;
  companyName: string;
  status: string;
  source: 'autotask' | 'manual';
  contactName?: string;
  phone?: string;
  notes?: string;
  createdBy?: string;
  createdDate?: string;
}

/**
 * Get all custom clients
 */
export async function getCustomClients(): Promise<CustomClient[]> {
  return fetchWithAuth('/custom-clients');
}

/**
 * Create a new custom client
 */
export async function createCustomClient(client: {
  companyName: string;
  contactName?: string;
  phone?: string;
  notes?: string;
}): Promise<CustomClient> {
  return fetchWithAuth('/custom-clients', {
    method: 'POST',
    body: JSON.stringify(client),
  });
}

/**
 * Update a custom client
 */
export async function updateCustomClient(
  clientId: string,
  updates: {
    companyName?: string;
    contactName?: string;
    phone?: string;
    notes?: string;
  }
): Promise<CustomClient> {
  return fetchWithAuth(`/custom-clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a custom client
 */
export async function deleteCustomClient(clientId: string): Promise<void> {
  return fetchWithAuth(`/custom-clients/${clientId}`, {
    method: 'DELETE',
  });
}

/**
 * Create custom client in AutoTask
 */
export async function createClientInAutotask(
  clientId: string
): Promise<{ autotaskId: number; message: string }> {
  return fetchWithAuth(`/custom-clients/${clientId}/create-in-autotask`, {
    method: 'POST',
  });
}

// ============================================================================
// Company Admin Functions
// ============================================================================

/**
 * Company with billing info for admin view
 */
export interface CompanyAdmin {
  id: string;
  autoTaskId: number;
  companyName: string;
  status: string;
  source?: 'autotask' | 'manual';
  billingBlocked?: boolean;
  billingBlockedBy?: string;
  billingBlockedAt?: string;
  billingBlockedReason?: string;
}

/**
 * Get all companies for admin view (includes billing block info)
 */
export async function getCompaniesAdmin(params?: {
  search?: string;
  blockedOnly?: boolean;
}): Promise<CompanyAdmin[]> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.blockedOnly) searchParams.set('blockedOnly', 'true');
  const query = searchParams.toString();
  return fetchWithAuth(`/companies/admin${query ? `?${query}` : ''}`);
}

/**
 * Toggle billing block for a company
 */
export async function toggleBillingBlock(
  companyId: string | number,
  blocked: boolean,
  reason?: string
): Promise<CompanyAdmin> {
  return fetchWithAuth(`/companies/${companyId}/billing-block`, {
    method: 'PATCH',
    body: JSON.stringify({ blocked, reason }),
  });
}

// ============================================================================
// Diagnostic Session API (FixThis Integration)
// ============================================================================

/**
 * Device from UMT-TAM (Datto RMM agent)
 */
export interface TamDevice {
  uid: string;
  hostname: string;
  deviceType: {
    category: string;
    type: string;
  };
  operatingSystem?: string;
  status: string;
  intIpAddress?: string;
  extIpAddress?: string;
  lastSeen?: string;
  lastUser?: string;
  domain?: string;
}

/**
 * Customer from UMT-TAM
 */
export interface TamCustomer {
  id: string;
  name: string;
  autotaskCompanyId?: string;
  dattoSiteUid?: string;
  dattoSiteName?: string;
}

/**
 * Response for customer devices lookup
 */
export interface CustomerDevicesResponse {
  customer: TamCustomer;
  devices: TamDevice[];
}

/**
 * Diagnostic session status
 */
export type DiagnosticSessionStatus =
  | 'pending'      // Created, waiting for device to run one-liner
  | 'collecting'   // Device is collecting diagnostic data
  | 'analyzing'    // SecureScan is analyzing the data
  | 'ready'        // Analysis complete, findings available
  | 'active'       // Tech has started interactive session
  | 'completed'    // Session completed successfully
  | 'expired'      // Session timed out
  | 'failed';      // Something went wrong

/**
 * Diagnostic findings from AI analysis
 */
export interface DiagnosticFindings {
  summary: string;
  rootCause: string;
  recommendations: string[];
  severity: 'info' | 'warning' | 'critical';
  systemInfo?: {
    os: string;
    hostname: string;
    domain?: string;
    lastBoot?: string;
    uptime?: string;
  };
  artifactsCollected?: string[];
}

/**
 * Diagnostic session
 */
export interface DiagnosticSession {
  id: string;
  ticketId: number;
  ticketNumber: string;
  companyName: string;
  deviceUid: string;
  deviceHostname: string;
  techNotes: string;
  status: DiagnosticSessionStatus;
  triggerType: 'urgent' | 'scheduled';
  sessionToken: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string;
  analysisCompletedAt?: string;
  completedAt?: string;
  findings?: DiagnosticFindings;
  errorMessage?: string;
}

/**
 * Response when creating a diagnostic session
 */
export interface CreateSessionResponse {
  sessionId: string;
  sessionToken: string;
  oneLiner: string;
  status: DiagnosticSessionStatus;
  expiresAt: string;
}

/**
 * Get devices for a company from UMT-TAM
 */
export async function getDiagnosticDevices(companyId: number): Promise<CustomerDevicesResponse> {
  return fetchWithAuth<CustomerDevicesResponse>(`/diagnostic/devices/${companyId}`);
}

/**
 * Create a new diagnostic session
 */
export async function createDiagnosticSession(data: {
  ticketId: number;
  ticketNumber: string;
  autotaskCompanyId: string;
  companyName: string;
  deviceUid: string;
  deviceHostname: string;
  dattoSiteUid: string;
  ticketDescription: string;
  techNotes: string;
  triggerType: 'urgent' | 'scheduled';
}): Promise<CreateSessionResponse> {
  return fetchWithAuth<CreateSessionResponse>('/diagnostic/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get a diagnostic session by ID
 */
export async function getDiagnosticSession(sessionId: string): Promise<DiagnosticSession> {
  return fetchWithAuth<DiagnosticSession>(`/diagnostic/sessions/${sessionId}`);
}

/**
 * Get diagnostic sessions for a ticket
 */
export async function getDiagnosticSessionsForTicket(ticketId: number): Promise<DiagnosticSession[]> {
  return fetchWithAuth<DiagnosticSession[]>(`/diagnostic/sessions?ticketId=${ticketId}`);
}

// ============================================================================
// Push Notifications API
// ============================================================================

/**
 * Get the VAPID public key for push subscriptions
 */
export async function getVapidPublicKey(): Promise<string> {
  const result = await fetchWithAuth<{ vapidPublicKey: string }>('/push/vapid-key');
  return result.vapidPublicKey;
}

/**
 * Register a push notification subscription
 */
export async function subscribePush(
  resourceId: number,
  subscription: PushSubscriptionJSON
): Promise<{ id: string }> {
  return fetchWithAuth<{ id: string }>('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ resourceId, subscription }),
  });
}

/**
 * Unregister a push notification subscription
 */
export async function unsubscribePush(
  resourceId: number,
  endpoint: string
): Promise<void> {
  await fetchWithAuth('/push/subscribe', {
    method: 'DELETE',
    body: JSON.stringify({ resourceId, endpoint }),
  });
}

// ============================================================================
// User Notification Settings API
// ============================================================================

/**
 * Get user's notification settings
 */
export async function getUserNotificationSettings(
  resourceId: number
): Promise<UserNotificationSettings> {
  return fetchWithAuth<UserNotificationSettings>(`/user-settings/${resourceId}`);
}

/**
 * Update user's notification settings
 */
export async function updateUserNotificationSettings(
  resourceId: number,
  settings: Partial<UserNotificationSettings>
): Promise<UserNotificationSettings> {
  return fetchWithAuth<UserNotificationSettings>(`/user-settings/${resourceId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// ============================================================================
// Tech Location API
// ============================================================================

/**
 * Report current tech location
 */
export async function reportTechLocation(data: {
  resourceId: number;
  resourceName?: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
}): Promise<void> {
  await fetchWithAuth('/tech-location', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get all tech locations (for map display)
 */
export async function getTechLocations(): Promise<TechLocation[]> {
  return fetchWithAuth<TechLocation[]>('/tech-locations');
}

export interface NotificationDiagnosticRow {
  resourceId: number;
  fullName: string;
  email: string;
  pushSubscriptionCount: number;
  lastLocationAt: string | null;
  locationConsent: boolean;
  notifyNewTickets: boolean;
  notifyAssignedTickets: boolean;
  notifyCustomerReplies: boolean;
  workHoursStart: number;
  workHoursEnd: number;
}

export async function getNotificationDiagnostics(): Promise<{ resources: NotificationDiagnosticRow[] }> {
  return fetchWithAuth<{ resources: NotificationDiagnosticRow[] }>('/admin/notification-diagnostics');
}

/**
 * Get company locations (geocoded companies with open tickets, or all)
 * @param status - Optional ticket status filter (e.g. 'New', 'In Progress')
 * @param companyTickets - Optional map of companyId -> ticket count
 * @param all - If true, return all geocoded companies (not just those with open tickets)
 */
export async function getCompanyLocations(status?: string, companyTickets?: Map<number, number>, all?: boolean): Promise<CompanyLocationResponse> {
  let queryParts: string[] = [];
  if (all) queryParts.push('all=true');
  if (status) queryParts.push(`status=${encodeURIComponent(status)}`);
  if (companyTickets && companyTickets.size > 0) {
    // Encode as "id:count,id:count,..."
    const entries = Array.from(companyTickets.entries()).map(([id, count]) => `${id}:${count}`).join(',');
    queryParts.push(`companyIds=${entries}`);
  }
  const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  // The API returns { data, missingAddresses, ... } inside the fetchWithAuth unwrap
  // But fetchWithAuth unwraps to result.data, so the actual shape is the inner data array
  // We need to use a raw fetch here to get the full response
  const url = `${API_BASE}/company-locations${params}`;
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`API Error ${response.status}: ${response.statusText}`);
  }
  const result = await response.json() as { success: boolean; data: CompanyLocation[]; missingAddresses: MissingCompany[]; geocodedThisRequest: number; remainingToGeocode: number };
  return {
    data: result.data || [],
    missingAddresses: result.missingAddresses || [],
    geocodedThisRequest: result.geocodedThisRequest || 0,
    remainingToGeocode: result.remainingToGeocode || 0,
  };
}

/**
 * Update a company's address (dual-writes to AutoTask + Cosmos DB)
 */
export async function updateCompanyAddress(
  companyId: number,
  address: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }
): Promise<{ companyId: number; companyName: string }> {
  return fetchWithAuth(`/companies/${companyId}/address`, {
    method: 'PATCH',
    body: JSON.stringify(address),
  });
}

/**
 * Fetch a company's address from AutoTask (company record + CompanyLocations fallback)
 * and update the local cache. Returns the address found.
 */
export async function fetchCompanyAddressFromAutoTask(
  companyId: number
): Promise<{ companyId: number; companyName: string; address1?: string; city?: string; state?: string; postalCode?: string; source: string }> {
  return fetchWithAuth(`/companies/${companyId}/fetch-autotask-address`, {
    method: 'POST',
  });
}

/**
 * Acknowledge customer reply on a ticket (clears the reply badge)
 */
export async function acknowledgeTicketReply(ticketId: number): Promise<void> {
  await fetchWithAuth(`/tickets/${ticketId}/acknowledge`, { method: 'PATCH' });
}

// ============================================================================
// Recurring Time Entry API
// ============================================================================

/**
 * Get recurring entry templates for a resource
 */
export async function getRecurringEntries(resourceId: number): Promise<RecurringTimeEntry[]> {
  return fetchWithAuth<RecurringTimeEntry[]>(`/recurring-entries?resourceId=${resourceId}`);
}

/**
 * Create a recurring entry template (client or activity)
 */
export async function createRecurringEntry(entry: {
  templateType: 'client' | 'activity';
  templateName: string;
  defaultResourceId: number;
  roleId?: number;
  summaryNotes: string;
  isBillable?: boolean;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  monthlyPattern?: 'dayOfMonth' | 'nthWeekday';
  nthOccurrence?: number;
  biweeklyAnchor?: string;
  // Client template fields
  companyId?: number;
  companyName?: string;
  bidJobNumber?: string;
  bidJobDescription?: string;
  hoursWorked?: number;
  monthlyHoursCap?: number;
  // Activity template fields
  clients?: Array<{ companyId: number; companyName: string; hoursWorked: number; bidJobNumber?: string; bidJobDescription?: string }>;
}): Promise<RecurringTimeEntry> {
  return fetchWithAuth<RecurringTimeEntry>('/recurring-entries', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/**
 * Update a recurring entry template
 */
export async function updateRecurringEntry(
  id: string,
  updates: Partial<RecurringTimeEntry> & { addClients?: Array<{ companyId: number; companyName: string; hoursWorked: number }> }
): Promise<RecurringTimeEntry> {
  return fetchWithAuth<RecurringTimeEntry>(`/recurring-entries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete (deactivate) a recurring entry template
 */
export async function deleteRecurringEntry(id: string): Promise<void> {
  await fetchWithAuth(`/recurring-entries/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Apply a recurring template for selected dates (batch create time entries)
 */
export async function applyRecurringEntry(
  templateId: string,
  dates: string[],
  resourceId: number,
  resourceName: string
): Promise<{ created: number; skipped?: number; entries: CalendarTimeEntry[]; errors?: string[]; ticketsReopened: string[] }> {
  return fetchWithAuth(`/recurring-entries/${templateId}/apply`, {
    method: 'POST',
    body: JSON.stringify({ dates, resourceId, resourceName }),
  });
}

/**
 * Progress data for a single recurring template
 */
export interface RecurringProgressEntry {
  applied: number;
  entryCounts: Record<string, number>;
}

/**
 * Get applied entry progress for all templates belonging to a resource for a given month
 */
export async function getRecurringEntriesProgress(
  resourceId: number,
  yearMonth: string
): Promise<Record<string, RecurringProgressEntry>> {
  return fetchWithAuth(`/recurring-entries/progress?resourceId=${resourceId}&month=${yearMonth}`);
}

/**
 * Get dates that already have entries from this template in a given month
 */
export async function getExistingDates(
  templateId: string,
  yearMonth: string
): Promise<{ dates: string[]; entryCounts?: Record<string, number> }> {
  return fetchWithAuth(`/recurring-entries/${templateId}/existing-dates?month=${yearMonth}`);
}

// ── Adjustment-sheet roundtrip ──────────────────────────────────────────────

export interface AdjustmentPreviewClient {
  ticketId: number;
  ticketNumber: string;
  companyName: string;
  templateHours: number;
  lastCycleHours: number | null;
  newHours: number;
  hoursChanged: boolean;
  templateNotes: string;
  lastCycleNotes: string | null;
  newNotes: string;
  notesChanged: boolean;
  status: 'ok' | 'unknown-client' | 'removed-from-template';
}

export interface AdjustmentPreview {
  templateId: string;
  templateName: string;
  targetMonth: string;
  lastCycle: string | null;
  clients: AdjustmentPreviewClient[];
}

/**
 * Trigger a download of the per-cycle adjustment sheet for an activity template.
 * The sheet has columns for last cycle's actuals + blank "this cycle" cells the
 * stakeholder fills in, then re-uploads.
 */
export async function downloadAdjustmentSheet(
  templateId: string,
  targetMonth: string,
  resourceId?: number,
): Promise<void> {
  // v1.86.2: pass the caller's resourceId explicitly. The server uses it to
  // filter the "My Hours This Month" column so it matches the Calendar sidebar.
  // Server-side auth introspection didn't work on the SWA-EasyAuth path.
  const rid = resourceId !== undefined ? `&resourceId=${resourceId}` : '';
  const url = `${API_BASE}/recurring-entries/${templateId}/adjustment-sheet?targetMonth=${targetMonth}${rid}`;
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const j = await response.json();
      msg = j.error || msg;
    } catch { /* not JSON */ }
    throw new Error(`Failed to download sheet: ${msg}`);
  }
  const blob = await response.blob();
  const cd = response.headers.get('content-disposition') || '';
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `adjustment-${targetMonth}.xlsx`;

  const dlUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = dlUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(dlUrl);
}

/**
 * Upload a filled-in adjustment sheet and get back a preview diff before applying.
 */
export async function previewAdjustmentSheet(
  templateId: string,
  file: File,
  targetMonthFallback?: string,
): Promise<AdjustmentPreview> {
  const fileBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  return fetchWithAuth<AdjustmentPreview>(`/recurring-entries/${templateId}/adjustment-sheet/preview`, {
    method: 'POST',
    body: JSON.stringify({ fileBase64, targetMonth: targetMonthFallback }),
  });
}

/**
 * Apply per-client adjustments for selected dates. The `adjustments` array is the
 * confirmed subset of the preview (filtered to status='ok' rows by the UI).
 */
export async function applyAdjustments(
  templateId: string,
  dates: string[],
  resourceId: number,
  resourceName: string,
  adjustments: Array<{
    ticketId: number;
    ticketNumber: string;
    companyName: string;
    hoursWorked: number;
    summaryNotes?: string;
  }>,
): Promise<{ created: number; skipped?: number; entries: CalendarTimeEntry[]; errors?: string[] }> {
  return fetchWithAuth(`/recurring-entries/${templateId}/adjustment-sheet/apply`, {
    method: 'POST',
    body: JSON.stringify({ dates, resourceId, resourceName, adjustments }),
  });
}

// ============================================================================
// Mileage & Expense API
// ============================================================================

/**
 * Get mileage entries for a month
 */
export async function getMileageEntries(
  year: number,
  month: number,
  resourceId?: number
): Promise<MileageEntry[]> {
  const params = new URLSearchParams({ year: String(year), month: String(month) });
  if (resourceId) params.set('resourceId', String(resourceId));
  return fetchWithAuth<MileageEntry[]>(`/mileage-entries?${params}`);
}

/**
 * Create a mileage entry
 */
export async function createMileageEntry(data: {
  resourceId: number;
  resourceName: string;
  dateWorked: string;
  startLocation: string;
  startCompanyId?: number;
  destination: string;
  destinationCompanyId?: number;
  estimatedMiles?: number;
  odometerStart: number;
  odometerEnd: number;
  ratePerMile: number;
  item?: string;
  ticketId?: number;
  ticketNumber?: string;
}): Promise<MileageEntry> {
  return fetchWithAuth<MileageEntry>('/mileage-entries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a mileage entry
 */
export async function updateMileageEntry(
  id: string,
  updates: Partial<MileageEntry>
): Promise<MileageEntry> {
  return fetchWithAuth<MileageEntry>(`/mileage-entries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a mileage entry
 */
export async function deleteMileageEntry(id: string): Promise<void> {
  await fetchWithAuth(`/mileage-entries/${id}`, { method: 'DELETE' });
}

/**
 * Get expense entries for a month
 */
export async function getExpenseEntries(
  year: number,
  month: number,
  resourceId?: number
): Promise<ExpenseEntry[]> {
  const params = new URLSearchParams({ year: String(year), month: String(month) });
  if (resourceId) params.set('resourceId', String(resourceId));
  return fetchWithAuth<ExpenseEntry[]>(`/expense-entries?${params}`);
}

/**
 * Create an expense entry
 */
export async function createExpenseEntry(data: {
  resourceId: number;
  resourceName: string;
  dateWorked: string;
  item: string;
  amount: number;
  companyId?: number;
  companyName?: string;
  ticketId?: number;
  ticketNumber?: string;
}): Promise<ExpenseEntry> {
  return fetchWithAuth<ExpenseEntry>('/expense-entries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an expense entry
 */
export async function updateExpenseEntry(
  id: string,
  updates: Partial<ExpenseEntry>
): Promise<ExpenseEntry> {
  return fetchWithAuth<ExpenseEntry>(`/expense-entries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete an expense entry
 */
export async function deleteExpenseEntry(id: string): Promise<void> {
  await fetchWithAuth(`/expense-entries/${id}`, { method: 'DELETE' });
}

/**
 * Get mileage distance estimate (Haversine x 1.3)
 */
export async function getMileageEstimate(
  from: 'office' | number,
  toCompanyId: number
): Promise<{ straightLine: number; estimatedMiles: number }> {
  return fetchWithAuth(`/mileage-estimate?from=${from}&toCompanyId=${toCompanyId}`);
}

// ============================================================================
// Intake Form System (authenticated tech endpoints)
// ============================================================================

export async function createIntakeSession(data: {
  ticketId: number;
  ticketNumber: string;
  companyId: number;
  companyName: string;
  endUserName: string;
  endUserEmail: string;
  contactId?: number;
}): Promise<{ id: string; token: string; status: string; expiresAt: string; intakeUrl: string }> {
  return fetchWithAuth('/intake/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getIntakeSessionsForTicket(ticketId: number): Promise<IntakeSession[]> {
  return fetchWithAuth(`/intake/sessions?ticketId=${ticketId}`);
}

/**
 * Get intake status summary for dashboard badges
 * Returns ticketId → latest intake status map
 */
export async function getIntakeStatusSummary(): Promise<Record<number, string>> {
  return fetchWithAuth<Record<number, string>>('/intake/status-summary');
}

export async function manageIntakeSession(id: string, action: 'invalidate' | 'resend'): Promise<unknown> {
  return fetchWithAuth(`/intake/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ action }),
  });
}

export async function getCompanyDevices(companyId: number): Promise<DeviceRegistration[]> {
  return fetchWithAuth(`/intake/company-devices?companyId=${companyId}`);
}

export async function getDeviceScanData(deviceId: number): Promise<DeviceScanResult | null> {
  return fetchWithAuth(`/intake/devices/${deviceId}/scan`);
}

export async function triggerDeviceScan(deviceId: number): Promise<{ jobId: string; message: string }> {
  return fetchWithAuth(`/intake/devices/${deviceId}/scan`, { method: 'POST' });
}

/**
 * Get all registered devices (admin view)
 */
export async function getAllRegisteredDevices(): Promise<DeviceRegistration[]> {
  return fetchWithAuth<DeviceRegistration[]>('/intake/all-devices');
}

// ──────────────────────────────────────────────────────────────────
// Reconcile Time Entries
// ──────────────────────────────────────────────────────────────────

export interface ReconcileAutoTaskEntry {
  id: number;
  ticketID: number;
  ticketNumber?: string;
  resourceID: number;
  dateWorked: string;
  hoursWorked: number;
  summaryNotes?: string;
  internalNotes?: string;
}

export interface ReconcileTimeTechEntry {
  TimeEntryId: number;
  UserId: number;
  ClientId: number;
  DateEntered: string;   // M/d/yyyy format
  Hours: number;
  Minutes: number;
  Description: string;
  DisplayText: string;
}

export interface ReconcileMeta {
  timetechEnabled: boolean;
  timetechUserMapped: boolean;
  year: number;
  month: number;
  resourceId: number;
  timetechNote?: string;
}

export interface ReconcileData {
  webapp: CalendarTimeEntry[];
  autotask: ReconcileAutoTaskEntry[];
  timetech: ReconcileTimeTechEntry[];
  meta: ReconcileMeta;
}

export async function getReconcileData(year: number, month: number, resourceId: number): Promise<ReconcileData> {
  return fetchWithAuth(`/reconcile/time-entries?year=${year}&month=${month}&resourceId=${resourceId}`);
}

/**
 * Import a single TimeTech entry into WebApp (Cosmos)
 */
export async function importTimeTechEntry(body: {
  timeEntryId: number;
  userId: number;
  clientId: number;
  dateEntered: string;
  hours: number;
  minutes: number;
  description: string;
  displayText: string;
}): Promise<{ id: string }> {
  return fetchWithAuth('/timetech/import-entry', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function createStandaloneTimeTechEntry(body: {
  resourceId: number;
  companyId: number;
  companyName?: string;
  timetechClientId?: number;
  dateWorked: string;
  hoursWorked: number;
  description: string;
  ticketNumber?: string;
  cosmosEntryId?: string;
}): Promise<{ timeEntryId: number; linkedCosmosId: string | null }> {
  return fetchWithAuth('/timetech/entries', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================================
// Contact-Approval Queue (Yumi crowdsourced write-back review)
// ============================================================================

export type ContactApprovalStatus = 'pending' | 'approved' | 'denied' | 'applied' | 'apply-failed';

export interface ContactApprovalChange {
  field: 'firstName' | 'lastName' | 'emailAddress' | 'phone' | 'mobilePhone' | 'title' | 'isActive';
  oldValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
}

export interface ContactApproval {
  id: string;
  kind: 'overwrite' | 'create';
  status: ContactApprovalStatus;
  requestedAt: string;
  requestedBy: string;
  contactId?: number;
  companyId: number;
  changes?: ContactApprovalChange[];
  newContact?: {
    firstName: string;
    lastName: string;
    emailAddress?: string;
    phone?: string;
    mobilePhone?: string;
    title?: string;
  };
  supportSessionCode?: string;
  callerAffirmation?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  denialReason?: string;
  appliedAt?: string;
  appliedContactId?: number;
  applyError?: string;
}

export async function listContactApprovals(status?: ContactApprovalStatus): Promise<ContactApproval[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchWithAuth<ContactApproval[]>(`/contact-approvals${q}`);
}

export async function getContactApproval(id: string): Promise<ContactApproval> {
  return fetchWithAuth<ContactApproval>(`/contact-approvals/${encodeURIComponent(id)}`);
}

export async function approveContactApproval(id: string): Promise<ContactApproval> {
  return fetchWithAuth<ContactApproval>(`/contact-approvals/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
}

export async function denyContactApproval(id: string, reason?: string): Promise<ContactApproval> {
  return fetchWithAuth<ContactApproval>(`/contact-approvals/${encodeURIComponent(id)}/deny`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
